import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { search } from "../utils/searchIndex";
import Icon from "./Icon";

/**
 * Site header with search.
 *
 * The site had no header at all — every route relied on a breadcrumb back
 * to Home and a footer at the bottom of a long page. That left 139 camera
 * hubs with no internal link pointing at them from anywhere, and left a
 * visitor who landed on one deep page from search with no way sideways.
 *
 * Search runs entirely in the browser against an index built from data
 * already in the bundle. No service, no request, works offline — which
 * matters on this site more than most, since a good share of its use is on
 * a phone somewhere with poor signal.
 */
export default function Header() {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  // A scanned page is a single card someone is holding at arm's length on
  // set. A sticky site header with a search box on top of it is the wrong
  // furniture — it costs ~61px of the little vertical room a phone has,
  // and offers navigation nobody standing behind a camera wants. The page
  // is noindex too, so there's no crawl argument for keeping it either.
  const chromeless = location.pathname.startsWith("/view/");

  const results = query.trim().length >= 2 ? search(query, 8) : [];

  // Close the results when the route changes, or the panel hangs around
  // over the page someone just navigated to.
  useEffect(() => {
    setOpen(false);
    setQuery("");
  }, [location.pathname]);

  useEffect(() => {
    const onClickAway = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, []);

  const go = (path) => {
    setOpen(false);
    setQuery("");
    navigate(path);
  };

  const onKeyDown = (e) => {
    if (!open || !results.length) {
      if (e.key === "Enter" && query.trim()) go(`/search?q=${encodeURIComponent(query.trim())}`);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[active] ? results[active].path : `/search?q=${encodeURIComponent(query.trim())}`);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  if (chromeless) return null;

  return (
    <header className="site-header">
      <div className="header-inner">
        <Link to="/" className="header-brand">
          <Icon name="aperture" size={19} />
          <span>Filtspot</span>
        </Link>

        <nav className="header-nav" aria-label="Main">
          <Link to="/cameras">Cameras</Link>
          <Link to="/gallery">Settings</Link>
          <Link to="/builder">Builder</Link>
        </nav>

        <div className="header-search" ref={boxRef}>
          <label className="visually-hidden" htmlFor="site-search">Search cameras and settings</label>
          <div className="search-field">
            <Icon name="focus" size={15} />
            <input
              id="site-search"
              type="search"
              placeholder="Search a camera or setting"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
                setActive(0);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={onKeyDown}
              autoComplete="off"
              role="combobox"
              aria-expanded={open && results.length > 0}
              aria-controls="search-results"
            />
          </div>

          {open && results.length > 0 && (
            <ul className="search-results" id="search-results" role="listbox">
              {results.map((r, i) => (
                <li key={r.path + r.title} role="option" aria-selected={i === active}>
                  <button
                    type="button"
                    className={i === active ? "is-active" : ""}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(r.path)}
                  >
                    <span className="result-kind data">{r.kind}</span>
                    <span className="result-title">{r.title}</span>
                    <span className="result-sub">{r.subtitle}</span>
                  </button>
                </li>
              ))}
              <li className="search-all">
                <button type="button" onClick={() => go(`/search?q=${encodeURIComponent(query.trim())}`)}>
                  See all results for "{query.trim()}"
                </button>
              </li>
            </ul>
          )}

          {open && query.trim().length >= 2 && results.length === 0 && (
            <div className="search-results search-empty">
              Nothing matches "{query.trim()}". Try the camera's model name, or a setting like
              shutter, ISO or ND.
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
